import React from 'react';
import {
    MapPinIcon,
    CameraIcon,
    BugAntIcon,
    BeakerIcon,
    ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import type { Observation } from './types';

interface ObservationListProps {
    observations: Observation[];
    compact?: boolean;
}

const severityTone: Record<string, string> = {
    low: 'bg-slate-100 text-slate-600',
    medium: 'bg-amber-100 text-amber-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800',
};

const FieldValue: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => {
    const has = value !== undefined && value !== null && String(value) !== '';
    return (
        <div>
            <p className="text-[10px] font-black uppercase tracking-wide text-slate-400">{label}</p>
            <p className={`text-sm ${has ? 'font-semibold text-slate-800' : 'text-slate-400'}`}>{has ? value : '—'}</p>
        </div>
    );
};

const photoCount = (observation: Observation): number =>
    Array.isArray(observation.photo_urls) ? observation.photo_urls.length : 0;

/**
 * Sample-point observations from reports, with high/critical severity
 * highlighted and pest / disease / soil / weed fields surfaced clearly.
 */
const ObservationList: React.FC<ObservationListProps> = ({ observations, compact = false }) => {
    if (!observations || observations.length === 0) {
        return (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-400">
                No sample-point observations recorded.
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {observations.map((observation) => {
                const severity = String(observation.severity_level || '').toLowerCase();
                const highlighted = observation.alert_triggered || severity === 'high' || severity === 'critical';
                return (
                    <div
                        key={observation.id}
                        className={`rounded-xl border p-4 ${
                            highlighted ? 'border-orange-200 bg-orange-50/60' : 'border-slate-200 bg-white'
                        }`}
                    >
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-black text-slate-800">
                                {observation.sample_point_number
                                    ? `Sample point ${observation.sample_point_number}`
                                    : 'Sample point'}
                            </span>
                            <span
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-black capitalize ${
                                    severityTone[severity] || 'bg-slate-100 text-slate-600'
                                }`}
                            >
                                {severity || 'unassessed'}
                            </span>
                            {observation.alert_triggered && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-black text-red-700">
                                    <ExclamationTriangleIcon className="h-3 w-3" />
                                    Alert
                                </span>
                            )}
                        </div>

                        <div className={`mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 ${compact ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}>
                            <FieldValue label="Crop stage" value={observation.crop_stage} />
                            <FieldValue label="Crop condition" value={observation.crop_condition} />
                            <FieldValue label="Soil moisture" value={observation.soil_moisture} />
                            <FieldValue label="Weed pressure" value={observation.weed_pressure} />
                            <FieldValue label="Damaged area (ha)" value={observation.damaged_area} />
                            <FieldValue label="Follow-up" value={observation.recommended_follow_up} />
                        </div>

                        <div className="mt-3 flex flex-wrap gap-4">
                            <div className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                                <BugAntIcon className="h-4 w-4 text-slate-400" />
                                <span className="font-semibold">Pest:</span>
                                {observation.pest_presence ? (
                                    <span>
                                        {observation.pest_type || 'Present'}
                                        {observation.pest_severity ? ` · ${observation.pest_severity}` : ''}
                                    </span>
                                ) : (
                                    <span className="text-slate-400">None detected</span>
                                )}
                            </div>
                            <div className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                                <BeakerIcon className="h-4 w-4 text-slate-400" />
                                <span className="font-semibold">Disease:</span>
                                {observation.disease_presence ? (
                                    <span>
                                        {observation.disease_type || 'Present'}
                                        {observation.disease_severity ? ` · ${observation.disease_severity}` : ''}
                                    </span>
                                ) : (
                                    <span className="text-slate-400">None detected</span>
                                )}
                            </div>
                            {(observation.gps_lat || observation.gps_lng) && (
                                <div className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                                    <MapPinIcon className="h-4 w-4 text-slate-400" />
                                    <span className="font-mono text-xs">
                                        {Number(observation.gps_lat).toFixed(5)}, {Number(observation.gps_lng).toFixed(5)}
                                        {observation.gps_accuracy_m ? ` ±${Number(observation.gps_accuracy_m).toFixed(0)}m` : ''}
                                    </span>
                                </div>
                            )}
                            {photoCount(observation) > 0 && (
                                <div className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                                    <CameraIcon className="h-4 w-4 text-slate-400" />
                                    <span>
                                        {photoCount(observation)} photo{photoCount(observation) === 1 ? '' : 's'}
                                    </span>
                                </div>
                            )}
                        </div>

                        {observation.comment && (
                            <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                                {observation.comment}
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ObservationList;
